export default {
  article: {
    metaTitle: 'Guia de Basetao Spreadsheet 2026: como organizar suas compras',
    metaDescription:
      'Aprenda a criar e usar uma planilha de Basetao para organizar links de produtos, vendedores, fotos de QC e detalhes de envio em um só lugar.',
    breadcrumbHome: 'Início',
    breadcrumbCurrent: 'Guia de Basetao Spreadsheet',
    title: 'O guia completo de Basetao Spreadsheet (2026)',
    subtitle:
      'Um guia prático para criar e usar uma planilha de Basetao para organizar links de produtos, vendedores, fotos de QC e detalhes de envio.',
    authorLabel: 'Por',
    author: 'Equipe Basetao Spreadsheet',
    updatedLabel: 'Última atualização',
    updated: 'Agosto de 2026',
    readingTime: '8 min de leitura',
    tocLabel: 'Nesta página',
    intro: {
      heading: 'Introdução',
      p1: 'Comprar por meio de um agente de compras chinês como a Basetao abre as portas para uma enorme variedade de produtos — de tênis e roupas a acessórios e itens do dia a dia. Mas, quando seu carrinho começa a encher, acompanhar cada item, vendedor, preço e foto de QC pode rapidamente se tornar cansativo.',
      p2: 'É aí que entra uma planilha de Basetao. É uma forma simples e organizada de registrar tudo o que você compra, para sempre saber o que pediu, onde está e quanto custou. Este guia mostra tudo o que você precisa para criar e usar uma de forma eficaz.',
    },
    whatIsBasetao: {
      heading: 'O que é a Basetao?',
      p1: 'A Basetao é um agente de compras que ajuda compradores internacionais a adquirir itens de plataformas chinesas como Taobao, 1688 e Weidian. Ela atua como uma camada intermediária: você cola um link de produto, o agente compra o item para você, armazena no depósito dele e depois envia para o seu país. No caminho, você pode solicitar fotos de QC (controle de qualidade) para confirmar que o item está correto antes do envio.',
    },
    whatIsSpreadsheet: {
      heading: 'O que é uma planilha de Basetao?',
      p1: 'Uma planilha de Basetao é um documento compartilhado — geralmente criado no Google Sheets ou no Excel — que lista cada produto em que você está interessado ou que pediu pelo agente. Ela normalmente registra o link do produto, o vendedor, o preço, o tamanho ou a cor, o status de QC e o progresso do envio.',
      p2: 'Pense nela como o seu centro de controle pessoal para uma compra na Basetao. Em vez de fazer malabarismos com dezenas de abas do navegador e mensagens de chat, você mantém tudo em um só lugar que pode atualizar e revisar de relance.',
    },
    why: {
      heading: 'Por que usar uma planilha de Basetao?',
      p1: 'Uma planilha bem mantida economiza tempo e evita erros caros. Estes são os principais benefícios:',
      items: [
        'Acompanhe muitos itens de uma vez — especialmente útil para compras grandes.',
        'Compare preços e vendedores antes de se comprometer com uma compra.',
        'Registre os resultados de QC para decidir rapidamente se mantém ou devolve um item.',
        'Monitore o status do depósito e do envio sem vasculhar os registros do chat.',
        'Calcule um custo total realista, incluindo taxas do agente e envio.',
      ],
    },
    whatToInclude: {
      heading: 'O que incluir na sua planilha',
      p1: 'As colunas exatas dependem de como você compra, mas esses campos são úteis para quase todo mundo:',
      items: [
        'Link do produto — a URL original para reabrir o anúncio a qualquer momento.',
        'Nome do vendedor — para comparar a qualidade entre lojas diferentes.',
        'Preço — na moeda local e na sua própria moeda.',
        'Tamanho, cor e quantidade — para evitar pedir a variante errada.',
        'Status de QC e link da foto — para acompanhar quais itens passaram na inspeção.',
        'Status do depósito — chegou, armazenado ou pronto para envio.',
        'Método de envio e número de rastreamento — para a entrega final.',
      ],
    },
    structure: {
      heading: 'Como estruturar sua planilha',
      p1: 'Um layout de colunas claro torna sua planilha fácil de ler e filtrar. Uma estrutura inicial prática se parece com isto:',
      columns: [
        { col: 'Nome do produto', desc: 'Um nome curto e claro para encontrar itens rapidamente.' },
        { col: 'Link do produto', desc: 'A URL completa para reabrir o anúncio quando necessário.' },
        { col: 'Vendedor', desc: 'O nome da loja, útil para comparar a qualidade.' },
        { col: 'Preço', desc: 'Preço local e preço convertido na sua moeda.' },
        { col: 'Tamanho / cor', desc: 'A variante exata que você planeja comprar.' },
        { col: 'QC', desc: 'Um rápido sim/não ou um link para as fotos de QC.' },
        { col: 'Status', desc: 'Pedido, no depósito ou enviado.' },
      ],
    },
    tips: {
      heading: 'Dicas para se manter organizado',
      items: [
        'Use uma linha por item para poder filtrar e ordenar com facilidade.',
        'Codifique as linhas por cor conforme o status para ver o progresso de relance.',
        'Mantenha o link original do produto — os anúncios podem mudar ou desaparecer.',
        'Atualize a planilha logo após cada ação, em vez de confiar na memória.',
        'Faça backup da planilha ou compartilhe-a com você mesmo na nuvem.',
      ],
    },
    mistakes: {
      heading: 'Erros comuns a evitar',
      items: [
        'Pular o QC e só descobrir problemas depois que o item é enviado.',
        'Esquecer de anotar o tamanho ou a cor exatos, levando a pedidos errados.',
        'Não acompanhar os custos de envio, o que torna o total final uma surpresa.',
        'Usar nomes de produtos pouco claros que depois são impossíveis de buscar.',
        'Deixar a planilha desatualizada, de modo que ela não reflita mais a realidade.',
      ],
    },
    faq: {
      heading: 'Perguntas frequentes',
      items: [
        {
          q: 'Uma planilha de Basetao é o mesmo que o site oficial da Basetao?',
          a: 'Não. É uma ferramenta de organização independente. Não é afiliada nem faz parte do site oficial da Basetao, salvo indicação explícita em contrário.',
        },
        {
          q: 'Preciso de uma planilha para um pedido pequeno?',
          a: 'Não necessariamente, mas mesmo poucos itens ficam mais fáceis de gerenciar quando os links, preços e status de QC estão todos em um só lugar.',
        },
        {
          q: 'Qual ferramenta devo usar para criar a planilha?',
          a: 'Google Sheets e Excel são ambos populares. O Google Sheets é fácil de acessar de qualquer dispositivo e simples de compartilhar.',
        },
        {
          q: 'O que fazer se um anúncio de produto desaparecer?',
          a: 'Mantenha o nome do vendedor e uma descrição clara na sua planilha e entre em contato com o agente usando o link original, se ainda o tiver.',
        },
        {
          q: 'Este site vende produtos diretamente?',
          a: 'Não. Este site é um recurso informativo. As categorias de produtos direcionam para o site principal, onde você pode explorar e comprar.',
        },
      ],
    },
    cta: {
      title: 'Pronto para organizar sua próxima compra na Basetao?',
      desc: 'Explore as categorias de produtos selecionadas ou vá ao site principal para percorrer todo o catálogo.',
    },
  },
};